/* eslint-disable @nx/enforce-module-boundaries */
import { provideHttpClient, withInterceptors, HttpClient } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection, inject, provideAppInitializer } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withDebugTracing,
  withRouterConfig,
} from '@angular/router';
import * as AuthStore from '@kanzi-apes/kanzi-authenticator';
import { KanziUtilsModule } from '@kanzi-apes/kanzi-utils';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { environment } from '../environments/environment';
import * as LayoutReducer from './+state/layout/layout.reducer';
import { metaReducers, reducers } from './+state/reducers';
import { appRoutes } from './app.routes';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  provideAngularQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { ConfirmationService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';

/**
 * APP_INITIALIZER factory that preloads translations before the app renders.
 * @param translate - TranslateService instance
 * @returns Promise that resolves when translations are loaded
 */
export function initializeTranslations(translate: TranslateService): () => Promise<void> {
  return () => {
    const lang = localStorage.getItem('lang') || 'es';
    translate.setDefaultLang('es');
    return firstValueFrom(translate.use(lang)).then(() => {});
  };
}

// Crear el módulo de traducción aparte
const translateModule = TranslateModule.forRoot({
  loader: provideTranslateHttpLoader(),
  defaultLanguage: 'es',
});

export const appConfig: ApplicationConfig = {
  providers: [
    ConfirmationService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withDebugTracing(),
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    provideAnimations(),
    provideHttpClient(withInterceptors([AuthStore.TokenWmsInterceptorFn])),
    importProvidersFrom(KanziUtilsModule.forRoot({ wmsEnv: environment })),
    importProvidersFrom(
      StoreModule.forRoot(reducers, {
        metaReducers,
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      })
    ),
    importProvidersFrom(
      StoreDevtoolsModule.instrument({
        name: 'Kanzi WMS DevTools',
        logOnly: environment.production,
        maxAge: 30,
        connectInZone: true,
      })
    ),
    importProvidersFrom(EffectsModule.forRoot([])),
    importProvidersFrom(
      StoreModule.forFeature(
        LayoutReducer.LAYOUT_FEATURE_KEY,
        LayoutReducer.layoutReducer
      )
    ),
    importProvidersFrom(
      StoreModule.forFeature(AuthStore.AUTH_FEATURE_KEY, AuthStore.authReducer)
    ),
    importProvidersFrom(EffectsModule.forFeature(AuthStore.AuthEffects)),
    
    // 👇 Usar la variable creada
    importProvidersFrom(translateModule),
    provideAppInitializer(() => {
        const initializerFn = (initializeTranslations)(inject(TranslateService));
        return initializerFn();
      }),
    
    provideAngularQuery(new QueryClient()),
  ],
};